// Firebase Web Push (FCM) — client SDK init + helpers.
// Every entry point is wrapped so unsupported browsers (Safari iOS, etc.)
// or denied-permission flows fail silently — never throws, never toasts.
import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

export const FIREBASE_ENABLED = Boolean(firebaseConfig.apiKey && VAPID_KEY);

export function getFirebaseApp() {
  try {
    if (!FIREBASE_ENABLED) return null;
    return getApps()[0] || initializeApp(firebaseConfig);
  } catch {
    return null;
  }
}

async function safeIsSupported() {
  try { return await isSupported(); } catch { return false; }
}

async function registerSW() {
  try {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
    // Explicit root scope so the installed PWA + Chrome browser share the same
    // registration (avoids "two SWs / two tokens" surprises on Android).
    const reg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      scope: "/",
      updateViaCache: "none",
    });
    // Force activation of any new SW immediately (skipWaiting + clients.claim
    // are set in the SW itself; this triggers the update check).
    try { await reg.update(); } catch { /* noop */ }
    return reg;
  } catch {
    return null;
  }
}

/**
 * Request permission and return an FCM registration token. Returns null on
 * any failure (unsupported browser, permission denied, network error).
 * Never throws, never displays UI.
 */
export async function requestFcmToken() {
  try {
    if (!FIREBASE_ENABLED) return null;
    if (!(await safeIsSupported())) return null;
    if (typeof Notification === "undefined") return null;

    let perm = Notification.permission;
    if (perm !== "granted") {
      try {
        perm = await Notification.requestPermission();
      } catch {
        return null;
      }
    }
    if (perm !== "granted") return null;

    const swReg = await registerSW();
    if (!swReg) return null;

    const app = getFirebaseApp();
    if (!app) return null;

    const messaging = getMessaging(app);
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });
    return token || null;
  } catch {
    return null;
  }
}

/**
 * Subscribe to foreground push messages. Returns an unsubscribe function.
 * No-op (and no error) when FCM is unsupported.
 */
export function onForegroundPush(handler) {
  try {
    if (!FIREBASE_ENABLED) return () => {};
    const app = getFirebaseApp();
    if (!app) return () => {};
    const messaging = getMessaging(app);
    return onMessage(messaging, (payload) => {
      try { handler(payload); } catch { /* swallow handler errors */ }
    });
  } catch {
    return () => {};
  }
}
